import React, { useState } from 'react';
import { Sparkles, MessageCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IcebreakerDialog } from './IcebreakerDialog';
import { useIcebreakerSuggestions } from '@/hooks/useIcebreakerSuggestions';
import { useGems } from '@/hooks/useGems';

interface IcebreakerSuggestionsProps {
  partnerId: string;
  partnerUsername: string;
  onSelectSuggestion: (text: string) => void;
}

export const IcebreakerSuggestions: React.FC<IcebreakerSuggestionsProps> = ({
  partnerId,
  partnerUsername,
  onSelectSuggestion
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { suggestions, isLoading, generateSuggestions, clearSuggestions } = useIcebreakerSuggestions();
  const { balance } = useGems();

  const handleConfirmGenerate = async () => {
    setDialogOpen(false);
    await generateSuggestions(partnerId);
  };

  const handleSelectSuggestion = (text: string) => {
    onSelectSuggestion(text);
    // Don't clear suggestions so user can pick another if they change their mind
  };

  const handleRegenerateClick = () => {
    setDialogOpen(true);
  };

  // Show suggestions if we have them
  if (suggestions.length > 0) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[60vh] px-4">
        <div className="text-center max-w-md mx-auto w-full">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-luxury-pinkBright/20 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-luxury-pinkBright" />
          </div>
          
          <h3 className="text-lg font-semibold text-white mb-2">
            Escolha uma sugestão
          </h3>
          <p className="text-white/60 text-sm mb-6">
            Toque em uma mensagem para usá-la
          </p>

          <div className="space-y-3 mb-6">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full p-4 text-left bg-white/5 hover:bg-white/10 
                         border border-white/10 hover:border-luxury-pinkBright/30 
                         rounded-xl transition-all duration-200 group"
              >
                <p className="text-white text-sm leading-relaxed group-hover:text-luxury-pinkBright">
                  "{suggestion}"
                </p>
              </button>
            ))}
          </div>

          <button
            onClick={handleRegenerateClick}
            className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white/70 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Gerar novas sugestões (1💎)
          </button>

          <IcebreakerDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            onConfirm={handleConfirmGenerate}
            availableGems={balance}
            targetUsername={partnerUsername}
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  // Show empty state with CTA
  return (
    <div className="flex-1 flex items-center justify-center min-h-[60vh] px-6">
      <div className="text-center max-w-md mx-auto">
        <div className="relative mb-8">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center border border-white/20 backdrop-blur-sm">
            <MessageCircle className="w-10 h-10 text-white/80" />
          </div>
          <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-luxury-pinkBright/30 animate-pulse"></div>
          <div className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full bg-white/30 animate-pulse delay-150"></div>
        </div>
        
        <h3 className="text-xl font-semibold text-white mb-3">
          Momento perfeito para começar
        </h3>
        
        <p className="text-white/70 mb-6 leading-relaxed">
          Quebre o gelo com {partnerUsername} de forma especial!
        </p>
        
        <Button
          onClick={() => setDialogOpen(true)}
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:opacity-90 
                   text-white font-medium py-6 rounded-xl mb-4"
        >
          {isLoading ? (
            <>
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              Gerando...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5 mr-2" />
              Sugerir quebra-gelo (1💎)
            </>
          )}
        </Button>

        <p className="text-white/40 text-sm">
          ou escreva sua própria mensagem abaixo
        </p>

        <IcebreakerDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onConfirm={handleConfirmGenerate}
          availableGems={balance}
          targetUsername={partnerUsername}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
